import { useState, useEffect, useCallback } from 'react';
import { Box } from '@chakra-ui/react';
import { Colors, Icon, Text } from '@mercator-gcp/grout';
import { onAuthStateChanged, User } from 'firebase/auth';
import { Spinner } from 'components/elements/Spinner';
import SSOButton from 'components/elements/SSOButton';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { FlexRow } from 'components/layout/FlexRow';
import { auth } from 'firebaseConfig';
import { useLinkAccountWithSSO } from 'hooks/useAuth';
import {
  AccountPageViewSections,
  AccountPageViewSubSections,
} from 'utils/analytics/constants';
import { trackLinkWithSSO } from 'utils/analytics/ssoAuth';
import { SSO_PROVIDERS } from 'utils/constants/ssoProviders';

const linkStatusStyle = {
  color: Colors.ForestGreen900,
  gap: 'padding_4',
};

const ssoButtonStyle = { width: 'auto', marginBottom: 'padding_12' };

const supportLinkStyle = {
  textDecoration: 'underline',
};

const CONNECT_WITH_MICROSOFT_BUTTON_TEXT = 'Sign in with Microsoft';

const LinkAccountWithSSO = () => {
  const { isLinking, linkAccountWithMicrosoft } = useLinkAccountWithSSO();

  const [user, setUser] = useState<User | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });

    return () => unsubscribe();
  }, []);

  const handleLinkAccountWithSSO = useCallback(() => {
    trackLinkWithSSO({
      clickAction: 'start',
      provider: SSO_PROVIDERS.MICROSOFT,
      clickText: CONNECT_WITH_MICROSOFT_BUTTON_TEXT,
      sectionName: AccountPageViewSections.ACCOUNT_SETTINGS,
      subSectionName: AccountPageViewSubSections.OVERVIEW,
    });
    linkAccountWithMicrosoft();
  }, [linkAccountWithMicrosoft]);

  if (!user) return <Spinner />;

  // providerData is an array of provider info objects
  const providers = user.providerData.map((p) => p.providerId);

  return (
    <FlexColumn sx={{ gap: 'padding_16' }}>
      <Text.Body>
        Connect your Mercator account with an email provider to enable single sign-on
        authentication.
      </Text.Body>
      <FlexRow>
        {providers.includes('microsoft.com') ? (
          <FlexRow sx={linkStatusStyle}>
            <Icon name={Icon.Name.CircleChecked} fill={Colors.ForestGreen900} size={18} />
            Your Microsoft account is connected
          </FlexRow>
        ) : (
          <Box>
            <SSOButton
              provider="microsoft"
              label={CONNECT_WITH_MICROSOFT_BUTTON_TEXT}
              onClick={() => handleLinkAccountWithSSO()}
              isLoading={isLinking}
              isDisabled={isLinking}
              sx={ssoButtonStyle}
            />
            <Text as="div">
              Having trouble connecting?{' '}
              <a
                href="https://intercom.help/mercator-ai/en/articles/12263452-how-to-connect-your-microsoft-account-to-mercator-ai"
                target="_blank"
                rel="noreferrer"
                style={supportLinkStyle}
              >
                Read our support guide
              </a>{' '}
              for steps to fix Microsoft issues.
            </Text>
          </Box>
        )}
      </FlexRow>
    </FlexColumn>
  );
};

export default LinkAccountWithSSO;
